export interface ParsedXlsxData {
	headers: string[];
	rows: string[][];
}

export interface RowLevelStats {
	rowsDetected: number;
	rowsReady: number;
	duplicates: number;
}

export interface FoundFieldMapping {
	foundField: string;
	assignedField: string;
}

/** API counts can arrive as strings at runtime — coerce before any arithmetic (web rule §6). */
export const toCount = (value?: number): number => Number(value ?? 0) || 0;

/**
 * Resolves the column index of a mapped foundField in the raw header row. The form rewrites
 * foundField names on manual re-selection (trim + "." → " "), so match both spellings.
 */
export const matchHeaderIndex = (headers: string[], foundField: string): number => {
	return headers.findIndex(
		(header) => header.trim() === foundField || header.trim().replaceAll(".", " ") === foundField
	);
};

/** First data row's value for a mapped column — the per-row preview in the mapping table. */
export const getFirstRowValue = (
	parsedData: ParsedXlsxData | undefined,
	foundField: string
): string => {
	if (!parsedData?.rows.length) {
		return "";
	}

	const columnIndex = matchHeaderIndex(parsedData.headers, foundField);

	return columnIndex >= 0 ? (parsedData.rows[0][columnIndex] ?? "") : "";
};

/**
 * Row-level XLS statistics computed client-side from the SheetJS parse — the screen-preview
 * endpoint only returns title-level figures. A duplicate is a data row whose full cell content
 * matches an earlier row.
 */
export const computeRowLevelStats = (parsedData?: ParsedXlsxData): RowLevelStats => {
	const rows = parsedData?.rows ?? [];
	const seen = new Set<string>();
	let duplicates = 0;

	for (const row of rows) {
		const key = row.join(" ");

		if (seen.has(key)) {
			duplicates += 1;
		} else {
			seen.add(key);
		}
	}

	return {
		rowsDetected: rows.length,
		rowsReady: rows.length - duplicates,
		duplicates,
	};
};
